import React, { useContext, createContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { isSameStationOverview } from "utils/stationOverview";
import { getStationList as getApiStationList, getStationDetail } from "services/api";
import { useAuth } from "./AuthProvider";
import { StatusCode } from "services/api/flagbags/types";
import { useVolunteerController } from "hooks/useVolunteerController";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import {
	initIndexedDbIfMissingStore,
	recoverIndexedDbIfMissingStoreError,
} from "utils/indexedDbRecovery";

const DataContext = createContext(null);

const DataProvider = ({ children }) => {
	const auth = useAuth();
	const volunteerController = useVolunteerController();
	const { isOnline } = useNetworkStatus();
	
	const [eventID, setEventID] = useState(localStorage.getItem("eventID") || null);
    const [flagbagCount, setFlagbagCount] = useState(0);
    const [flagbagPreassign, setFlagbagPreassign] = useState(0);
	const [stationID, setStationID] = useState(localStorage.getItem("stationID") || null);
	const [eventName, setEventName] = useState(localStorage.getItem("eventName") || null);
    const [startTime, setStartTime] = useState(localStorage.getItem("startTime") || null);
	const [stationList, setStationList] = useState([]);
	const [stationOverview, setStationOverview] = useState(null);
	const [isDataRestored, setIsDataRestored] = useState(false);
	const eventIDRef = useRef(eventID);
	eventIDRef.current = eventID;

	const db = initIndexedDbIfMissingStore("records");
	const stationDB = initIndexedDbIfMissingStore("station"); // 站點詳情
	const flagBagDB = initIndexedDbIfMissingStore("flagbag"); // 旗地數據
	const offlineDB = initIndexedDbIfMissingStore("offline"); // 離線數據

	// 初始化时从 IndexedDB 恢复数据
	useEffect(() => {
		let hasRetriedAfterDbInit = false;
		const restoreData = async () => {
			try {
				// 恢复 stationOverview
				const savedOverview = await stationDB.getByID("stationOverview");
				if (savedOverview) {
					setStationOverview(savedOverview);
					
					// 检查 flagBagDB 中的 flagbags 数量
					try {
						const allFlagbags = await flagBagDB.getAll();
						
						// 如果 flagBagDB 中没有数据，但 overview 中有，重新保存
						if (allFlagbags.length === 0 && savedOverview.stations) {
							let restoredCount = 0;
							
							for (const stationData of savedOverview.stations) {
								const flagbags = stationData.flagbags || [];
								
								const restorePromises = flagbags.map(flagbag => {
									const flagbagWithStation = {
										...flagbag,
										stationId: stationData.station.id,
										stationName: stationData.station.name,
										isOverviewOnly: true,
									};
									restoredCount++;
									return flagBagDB.update(flagbagWithStation);
								});
								
								await Promise.all(restorePromises);
							}
						}
					} catch (err) {
						console.warn("Failed to check/restore flagbags:", err);
					}
				}
				
			// 恢复 flagbag 统计数据
			const savedFlagbag = await flagBagDB.getByID("flagbag_metadata");
			if (savedFlagbag) {
				if (savedFlagbag.count !== undefined) {
					setFlagbagCount(savedFlagbag.count);
				}
				if (savedFlagbag.preassign !== undefined) {
					setFlagbagPreassign(savedFlagbag.preassign);
				}
			}
				
				setIsDataRestored(true);
			} catch (error) {
				if (!hasRetriedAfterDbInit) {
					const didRecover = await recoverIndexedDbIfMissingStoreError(error);
					if (didRecover) {
						hasRetriedAfterDbInit = true;
						await restoreData();
						return;
					}
				}
				console.error("Failed to restore data from IndexedDB:", error);
				setIsDataRestored(true);
			}
		};
		
		restoreData();
	}, []);

	useEffect(() => {
		if (!stationOverview?.stations || stationOverview.stations.length === 0) {
			return;
		}

		const firstStationId = stationOverview.stations[0].station.id;
		const hasCurrentStation = stationOverview.stations.some(
			(stationData) => stationData.station.id === stationID
		);

		if (!stationID || !hasCurrentStation) {
			setStationId(firstStationId);
		}
	}, [stationOverview, stationID]);

	useEffect(() => {
		if (stationOverview?.stations) {
			setStationList(stationOverview.stations);
		}
	}, [stationOverview]);

	// 當 eventID 變化時，獲取並緩存志願者數據（僅在線上模式）
	useEffect(() => {
		const fetchVolunteersData = async () => {
			if (!eventID || !isDataRestored) return;
			
			// 離線模式下跳過網絡請求
			if (!isOnline) {
				console.log("Offline mode: Skipping volunteer data fetch, will use cached data");
				return;
			}

			try {
				// 靜默獲取志願者數據並保存到 IndexedDB
				// 不需要等待結果，讓它在後台執行
				volunteerController.fetchAndSaveVolunteers(eventID).catch(error => {
					console.warn("Failed to fetch volunteers in background:", error);
					// 失敗不影響主流程
				});
			} catch (error) {
				console.warn("Error initiating volunteer fetch:", error);
			}
		};

		fetchVolunteersData();
	}, [eventID, isDataRestored, isOnline, volunteerController.fetchAndSaveVolunteers]);

	const setEventId = (id: string) => {
		setEventID(id);
		localStorage.setItem("eventID", id);
	};

	const getEventId = useCallback(() => {
		if (stationOverview?.event?.id) {
			return stationOverview.event.id;
		}
		return eventIDRef.current || null;
	}, [stationOverview]);

	const setStationId = (id: string) => {
		setStationID(id);
		localStorage.setItem("stationID", id);
		if (auth && auth.updateStationId) {
			auth.updateStationId(id);
		}
	};

	const getStationId = () => {
		return stationID || "";
	};

	const getStationList = () => {
		// 優先從 stationOverview 中獲取最新數據
		if (stationOverview?.stations && Array.isArray(stationOverview.stations)) {
			return stationOverview.stations.map(s => ({
				id: s.station.id,
				stationName: s.station.name,
				eventName: s.station.eventName,
				eventDate: s.station.eventDate,
			}));
		}
		// 回退到 stationList state
		if (stationList.length > 0) {
			return stationList;
		}
		return [];
	};

	const getStationName = () => {
		if (stationOverview?.stations && Array.isArray(stationOverview.stations)) {
			const station = stationOverview.stations.find(s => s.station.id === stationID);
			return station?.station.name || stationOverview.stations[0]?.station.name || null;
		}
		
		// 回退到 stationList
		return getStationList().find((station) => station.id === stationID)?.stationName || getStationList()[0]?.stationName || null;
	};

	const getEventList = () => {
		return auth?.getAccountData()?.eventList || [];
	};

	const getEventName = () => {
		// 優先從 stationOverview 中獲取
		if (stationOverview?.event?.name) {
			return stationOverview.event.name;
		}
		// 其次從 localStorage 中的 eventName
		if (eventName) return eventName;
		// 最後從 eventList 中查找
		return getEventList().find((event) => event.id === eventID)?.eventName || null;
	};

	const setFlagbagData = (data) => {
		flagBagDB.update(data).catch(err => {
			console.warn("Failed to save flagbag data to IndexedDB:", err);
		});
	};

	const getFlagbagData = () => {
		return flagBagDB.getByID("flagbag_metadata");
	};

    const setFlagBagCount = (count: number) => {
        setFlagbagCount(count);
        // Note: This is metadata, not an actual flagbag record
        // We store it with a special ID to avoid conflicts
        flagBagDB.update({
            id: "flagbag_metadata",
            count: count
        }).catch(err => {
            console.warn("Failed to save flagbag count to IndexedDB:", err);
        });
    };

    const getFlagBagCount = () => {
        return flagbagCount;
    };

    const setStartTimeData = (startTime: string) => {
        setStartTime(startTime);
        localStorage.setItem("startTime", startTime);
    };

    const getStartTimeData = () => {
        return startTime;
    };

    const setFlagBagPreassign = (preassign: number) => {
        setFlagbagPreassign(preassign);
        // Note: This is metadata, not an actual flagbag record
        // We store it with a special ID to avoid conflicts
        flagBagDB.update({
            id: "flagbag_metadata",
            preassign: preassign
        }).catch(err => {
            console.warn("Failed to save flagbag preassign to IndexedDB:", err);
        });
    };
    
    const getFlagBagPreassign = () => {
        return flagbagPreassign;
    };

	const persistStationOverview = useCallback((data) => {
		if (data?.event) {
			if (data.event.id && !eventIDRef.current) {
				setEventID(data.event.id);
				localStorage.setItem("eventID", data.event.id);
			}
			if (data.event.name) {
				setEventName(data.event.name);
				localStorage.setItem("eventName", data.event.name);
			}
			if (data.event.startTime) {
				setStartTime(data.event.startTime);
				localStorage.setItem("startTime", data.event.startTime);
			}
		}

		const overviewWithId = {
			...data,
			id: "stationOverview",
		};

		stationDB.update(overviewWithId).catch((err) => {
			console.warn("Failed to save overview to IndexedDB:", err);
		});

		if (data?.stations && Array.isArray(data.stations)) {
			(async () => {
				for (const stationData of data.stations) {
					const stationId = stationData.station.id;
					const flagbags = stationData.flagbags || [];

					const savePromises = flagbags.map(async (flagbag) => {
						try {
							const existingData = await flagBagDB.getByID(flagbag.id);

							if (existingData && existingData.groupID) {
								const updatedFlagbag = {
									...existingData,
									flagbagNum: flagbag.flagbagNum,
									status: flagbag.status,
									volunteerName: flagbag.volunteerName || existingData.volunteerName,
									bagType: flagbag.bagType,
									finalStationId: flagbag.finalStationId,
								};
								await flagBagDB.update(updatedFlagbag);
							} else {
								const flagbagWithStation = {
									...flagbag,
									stationId: stationId,
									stationName: stationData.station.name,
									isOverviewOnly: true,
								};
								await flagBagDB.update(flagbagWithStation);
							}
						} catch (err) {
							console.warn(`Failed to save flagbag ${flagbag.id}:`, err);
						}
					});

					await Promise.all(savePromises);
				}
			})();
		}
	}, [stationDB, flagBagDB]);

	const setStationOverviewData = useCallback((data) => {
		setStationOverview((prev) => {
			if (isSameStationOverview(prev, data)) {
				return prev;
			}
			persistStationOverview(data);
			return data;
		});
	}, [persistStationOverview]);

	const getStationOverview = () => {
		return stationOverview;
	};

	/**
	 * 从 stationOverview 计算当前选中站点的旗袋统计数据
	 * 直接使用 API 返回的 stats 对象，不再从 flagbags 数组统计
	 */
	const calculateStationStats = useMemo(() => {
		if (!stationOverview || !stationID) {
			return null;
		}

		const currentStation = stationOverview.stations?.find(
			s => s.station.id === stationID
		) || stationOverview.stations?.[0];

		if (!currentStation || !currentStation.stats) {
			return null;
		}

		const flagbags = currentStation.flagbags || [];
		const stats = currentStation.stats;

		// 旗袋总数保持从 flagbags 数组获取
		const total = flagbags.length;

		// 从 stats 对象获取统计数据
		const collectedCount = stats.collectedCount || 0;
		const collectedSubToBankCount = stats.collectedSubToBankCount || 0;
		const collectedEmptyCount = stats.collectedEmptyCount || 0;
		const collectedOtherCount = stats.collectedOtherCount || 0;
		const uncollectedCount = stats.uncollectedCount || 0;
		const lostOrLatedCount = stats.lostOrLatedCount || 0;
		const assignedCount = stats.assignedCount || 0;
		const spareFlagbagCount = stats.spareFlagbagCount || 0;
		const overCollectedCount = stats.overCollectedCount || 0;
		const underCollectedCount = stats.underCollectedCount || 0;
		const distributedOtherCount = flagbags.filter(f => f.status === 'distributed_other').length;
		// 遲交數量：stats 沒提供獨立欄位，從 flagbags 過濾
		const lateCount = flagbags.filter(f => f.status === 'late').length;
		// 遺失數量：stats 沒提供獨立欄位，從 flagbags 過濾
		const lossCount = flagbags.filter(f => f.status === 'lost').length;

		// 计算已收总数（从 collectedCount 获取）
		const receivedTotal = collectedCount;
		// 计算未收总数（未收 + 未收其他 + 遲交/遺失）
		const notReceivedTotal = uncollectedCount + distributedOtherCount + lostOrLatedCount;
		// 已分配/未出总数
		const assigned = assignedCount;
		// 後備总数
		const reserve = spareFlagbagCount;

		// 实收数量：优先使用 API 返回的 actualCollectedCount，如果没有则计算
		const actualCollected = stats.actualCollectedCount !== undefined 
			? stats.actualCollectedCount 
			: collectedCount - collectedSubToBankCount - collectedEmptyCount - collectedOtherCount;

		return {
			total,
			flagBagPreassign: assigned, // 括号里的数量不再计算 spareFlagbagCount
			receivedTotal,
			notReceivedTotal,
			receivedList: {
				[StatusCode.COLLECTED]: { label: "實收", value: actualCollected },
				[StatusCode.BANK]: { label: "入銀行", value: collectedSubToBankCount },
				[StatusCode.COLLECTED_EMPTY]: { label: "吉袋", value: collectedEmptyCount },
				[StatusCode.COLLECTED_OTHER]: { label: "已收（其他）", value: collectedOtherCount },
			},
			unreceivedList: {
				[StatusCode.DISTRIBUTED]: { label: "未收", value: uncollectedCount },
				[StatusCode.DISTRIBUTED_OTHER]: { label: "未收（其他）", value: distributedOtherCount },
			},
			extra: overCollectedCount,  // 多收（来自其他站点）
			less: underCollectedCount,  // 少收（由其他站点代收）
			assign: assigned,
			reserve: reserve,
			late: lateCount,
			loss: lossCount,
		};
	}, [stationOverview, stationID]);

	/**
	 * 获取所有站点的统计数据汇总
	 */
	const getAllStationsStats = useMemo(() => {
		if (!stationOverview?.stations) {
			return [];
		}

		return stationOverview.stations.map(stationData => {
			const flagbags = stationData.flagbags || [];
			
			const assigned = flagbags.filter(f => f.status === 'assigned' && f.bagType === 'volunteer').length;
			const unassigned = flagbags.filter(f => f.status === 'unassigned' && f.bagType === 'spare').length;
			const distributed = flagbags.filter(f => f.status === 'distributed').length;
			const collected = flagbags.filter(f => f.status === 'collected').length;
			const collectedBank = flagbags.filter(f => f.status === 'collected_bank').length;
			const collectedEmpty = flagbags.filter(f => f.status === 'collected_empty').length;
			const collectedOther = flagbags.filter(f => f.status === 'collected_other').length;
			const lost = flagbags.filter(f => f.status === 'lost').length;
			const late = flagbags.filter(f => f.status === 'late').length;

			return {
				stationId: stationData.station.id,
				stationName: stationData.station.name,
				total: flagbags.length,
				assigned,
				unassigned,
				distributed,
				collected,
				collectedBank,
				collectedEmpty,
				collectedOther,
				lost,
				late,
				receivedTotal: collected + collectedBank + collectedEmpty + collectedOther,
				notReceivedTotal: distributed + lost + late,
			};
		});
	}, [stationOverview]);

	const clearAllData = () => {
		localStorage.removeItem("eventID");
		localStorage.removeItem("eventName");
		localStorage.removeItem("stationID");
		
		setEventID(null);
		setStationID(null);
		setEventName(null);
		setStationList([]);
		setStationOverview(null);

		db.clear();
		flagBagDB.clear();
		stationDB.clear();
		offlineDB.clear();
	};

	const contextValue = useMemo(
		() => ({
			stationOverview,
			eventID,
			stationID,
			eventName,
			db,
			stationDB,
			flagBagDB,
			offlineDB,
			setEventId,
			getEventId,
			setStationId,
			getStationId,
			getStationList,
			getStationName,
			getEventList,
			getEventName,
			setFlagbagData,
			getFlagbagData,
			setStationOverviewData,
			getStationOverview,
			clearAllData,
			calculateStationStats,
			getAllStationsStats,
			setStartTimeData,
			getStartTimeData,
			setFlagBagCount,
			getFlagBagCount,
			setFlagBagPreassign,
			getFlagBagPreassign,
		}),
		[
			stationOverview,
			eventID,
			stationID,
			eventName,
			flagbagCount,
			flagbagPreassign,
			startTime,
			stationList,
			calculateStationStats,
			getAllStationsStats,
		],
	);

	return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};

export default DataProvider;

export const useData = () => {
	return useContext(DataContext);
};
